import {
  WEBGPU_SINGLE_PASS_VERTEX,
  setupWebGpuSinglePass,
  cleanupWebGpuSinglePass,
  applyWebGpuSinglePass
} from "./index-tvfexb7s.mjs";
import {
  assertEffectParamsObject,
  assertRequiredFiniteNumber
} from "./index-4fvp93ps.mjs";

// src/wave/index.ts
import { Internals as Internals2 } from "remotion";

// src/wave/wave-runtime.ts
import { Internals } from "remotion";

// src/wave/wave-shaders.ts
var WAVE_VS = `#version 300 es
layout(location = 0) in vec2 aPos;
layout(location = 1) in vec2 aUv;
out vec2 vUv;
void main() {
	vUv = aUv;
	gl_Position = vec4(aPos, 0.0, 1.0);
}
`;
var WAVE_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec2 uResolution;
uniform float uAmplitude;
uniform float uWavelength;
uniform float uPhase;
uniform int uDirection;

const float TWO_PI = 6.28318530718;

void main() {
	float wavelength = max(uWavelength, 0.001);
	vec2 srcUv = vUv;

	if (uDirection == 0) {
		float coordPx = vUv.x * uResolution.x;
		float offsetPx =
			sin((coordPx / wavelength) * TWO_PI + uPhase) * uAmplitude;
		srcUv.y -= offsetPx / uResolution.y;
	} else {
		float coordPx = vUv.y * uResolution.y;
		float offsetPx =
			sin((coordPx / wavelength) * TWO_PI + uPhase) * uAmplitude;
		srcUv.x -= offsetPx / uResolution.x;
	}

	fragColor = texture(uSource, srcUv);
}
`;

// src/wave/wave-runtime.ts
var { createWebGL2ContextError } = Internals;
var compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create WebGL shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
};
var linkProgram = (gl, vs, fs) => {
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create WebGL program");
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link failed: ${log ?? "(no log)"}`);
  }
  return program;
};
var createProgram = (gl, vertexSource, fragmentSource) => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = linkProgram(gl, vs, fs);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
};
var getWaveUniforms = (gl, program) => ({
  uSource: gl.getUniformLocation(program, "uSource"),
  uResolution: gl.getUniformLocation(program, "uResolution"),
  uAmplitude: gl.getUniformLocation(program, "uAmplitude"),
  uWavelength: gl.getUniformLocation(program, "uWavelength"),
  uPhase: gl.getUniformLocation(program, "uPhase"),
  uDirection: gl.getUniformLocation(program, "uDirection")
});
var createRgbaTexture = (gl) => {
  const texture = gl.createTexture();
  if (!texture) {
    throw new Error("Failed to create WebGL texture");
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
};
var setupWave = (target) => {
  const gl = target.getContext("webgl2", {
    premultipliedAlpha: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  if (!gl) {
    throw createWebGL2ContextError("wave effect");
  }
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  const program = createProgram(gl, WAVE_VS, WAVE_FS);
  const vao = gl.createVertexArray();
  if (!vao) {
    throw new Error("Failed to create WebGL vertex array");
  }
  gl.bindVertexArray(vao);
  const data = new Float32Array([
    -1,
    -1,
    0,
    0,
    1,
    -1,
    1,
    0,
    -1,
    1,
    0,
    1,
    1,
    1,
    1,
    1
  ]);
  const vbo = gl.createBuffer();
  if (!vbo) {
    throw new Error("Failed to create WebGL buffer");
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
  gl.bindVertexArray(null);
  const textureSource = createRgbaTexture(gl);
  return {
    gl,
    program,
    vao,
    vbo,
    textureSource,
    uniforms: getWaveUniforms(gl, program)
  };
};
var cleanupWave = (state) => {
  const { gl, program, vao, vbo, textureSource } = state;
  gl.deleteTexture(textureSource);
  gl.deleteBuffer(vbo);
  gl.deleteProgram(program);
  gl.deleteVertexArray(vao);
};
var drawFullscreenQuad = (state) => {
  const { gl, vao } = state;
  gl.bindVertexArray(vao);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.bindVertexArray(null);
};
var applyWave = ({
  state,
  source,
  width,
  height,
  flipSourceY,
  amplitude,
  wavelength,
  phase,
  direction
}) => {
  const { gl, program, textureSource, uniforms } = state;
  gl.viewport(0, 0, width, height);
  gl.activeTexture(gl.TEXTURE0);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipSourceY);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  if (uniforms.uSource)
    gl.uniform1i(uniforms.uSource, 0);
  if (uniforms.uResolution)
    gl.uniform2f(uniforms.uResolution, width, height);
  if (uniforms.uAmplitude)
    gl.uniform1f(uniforms.uAmplitude, amplitude);
  if (uniforms.uWavelength)
    gl.uniform1f(uniforms.uWavelength, wavelength);
  if (uniforms.uPhase)
    gl.uniform1f(uniforms.uPhase, phase);
  if (uniforms.uDirection)
    gl.uniform1i(uniforms.uDirection, direction === "horizontal" ? 0 : 1);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  drawFullscreenQuad(state);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.useProgram(null);
};

// src/wave/webgpu-shader.ts
var WAVE_WEBGPU_SHADER = `
${WEBGPU_SINGLE_PASS_VERTEX}
@group(0) @binding(0) var source: texture_2d<f32>;
@group(0) @binding(1) var sourceSampler: sampler;
@group(0) @binding(2) var<uniform> params: Params;
struct Params { resolutionAndWave: vec4f, phaseAndDirection: vec4f, }
@fragment fn fragment(input: VertexOutput) -> @location(0) vec4f {
  let resolution = params.resolutionAndWave.xy;
  let amplitude = params.resolutionAndWave.z;
  let wavelength = max(params.resolutionAndWave.w, 0.001);
  let phase = params.phaseAndDirection.x;
  var uv = input.uv;
  if (params.phaseAndDirection.y == 0.0) {
    let coord = input.uv.x * resolution.x;
    let offset = sin((coord / wavelength) * 6.28318530718 + phase) * amplitude;
    uv.y += offset / resolution.y;
  } else {
    let coord = (1.0 - input.uv.y) * resolution.y;
    let offset = sin((coord / wavelength) * 6.28318530718 + phase) * amplitude;
    uv.x -= offset / resolution.x;
  }
  return textureSampleLevel(source, sourceSampler, uv, 0);
}
`;

// src/wave/index.ts
var { createEffect } = Internals2;
var waveSchema = {
  phase: {
    type: "number",
    default: 0,
    description: "Phase",
    hiddenFromList: false
  },
  direction: {
    type: "enum",
    variants: {
      horizontal: {},
      vertical: {}
    },
    default: "horizontal",
    description: "Direction"
  },
  amplitude: {
    type: "number",
    min: 0,
    max: 500,
    step: 1,
    default: 60,
    description: "Amplitude",
    hiddenFromList: false
  },
  wavelength: {
    type: "number",
    min: 1,
    max: 2000,
    step: 1,
    default: 240,
    description: "Wavelength",
    hiddenFromList: false
  }
};
var resolve = (p) => ({
  phase: p.phase ?? 0,
  direction: p.direction ?? "horizontal",
  amplitude: p.amplitude ?? 60,
  wavelength: p.wavelength ?? 240
});
var assertOptionalFiniteNumber = (value, name) => {
  if (value === undefined) {
    return;
  }
  assertRequiredFiniteNumber(value, name);
};
var validateWaveParams = (params) => {
  assertEffectParamsObject(params, "Wave");
  assertOptionalFiniteNumber(params.phase, "phase");
  assertOptionalFiniteNumber(params.amplitude, "amplitude");
  assertOptionalFiniteNumber(params.wavelength, "wavelength");
  if (params.direction !== undefined && params.direction !== "horizontal" && params.direction !== "vertical") {
    throw new TypeError(`"direction" must be "horizontal" or "vertical", but got ${JSON.stringify(params.direction)}`);
  }
  const resolved = resolve(params);
  if (resolved.amplitude < 0) {
    throw new TypeError(`"amplitude" must be >= 0, but got ${JSON.stringify(resolved.amplitude)}`);
  }
  if (resolved.wavelength <= 0) {
    throw new TypeError(`"wavelength" must be > 0, but got ${JSON.stringify(resolved.wavelength)}`);
  }
};
var wave = createEffect({
  type: "dev.remotion.effects.wave",
  label: "wave()",
  documentationLink: "https://www.remotion.dev/docs/effects/wave",
  calculateKey: (params) => {
    const r = resolve(params);
    return `wave-${r.phase}-${r.direction}-${r.amplitude}-${r.wavelength}`;
  },
  implementations: {
    webgpu: Internals2.createEffectImplementation({
      setup: (target) => setupWebGpuSinglePass(target, "wave"),
      apply: ({ source, width, height, params, state, gpuDevice }) => {
        if (!gpuDevice) {
          throw new Error("A WebGPU device is required for the wave effect");
        }
        const r = resolve(params);
        const uniformData = new Float32Array([
          width,
          height,
          r.amplitude,
          r.wavelength,
          r.phase,
          r.direction === "horizontal" ? 0 : 1,
          0,
          0
        ]);
        applyWebGpuSinglePass({
          state,
          source,
          width,
          height,
          device: gpuDevice,
          shader: WAVE_WEBGPU_SHADER,
          uniformData
        });
      },
      cleanup: cleanupWebGpuSinglePass
    }),
    webgl2: Internals2.createEffectImplementation({
      setup: (target) => setupWave(target),
      apply: ({ source, width, height, params, state, flipSourceY }) => {
        const r = resolve(params);
        applyWave({
          state,
          source,
          width,
          height,
          flipSourceY,
          amplitude: r.amplitude,
          wavelength: r.wavelength,
          phase: r.phase,
          direction: r.direction
        });
      },
      cleanup: (state) => cleanupWave(state)
    })
  },
  schema: waveSchema,
  validateParams: validateWaveParams
});
export {
  wave
};
